// Keycloak login: OIDC authorization code + PKCE with a public client (oidc-client-ts).
// Issuer, client id and audience come from the runtime config.
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import type { Config } from '../config';

/** What the API client and the log stream need from the login: a current access token. */
export interface TokenSource {
  /** A valid access token, renewed when it expires within 30 s. */
  token(): Promise<string>;
  /** A fresh token even if the current one still looks valid (after 401 or a 4408 close). */
  renew(): Promise<string>;
}

export class Session implements TokenSource {
  private readonly mgr: UserManager;

  constructor(cfg: Config) {
    const redirect = window.location.origin + window.location.pathname;
    this.mgr = new UserManager({
      authority: cfg.issuer,
      client_id: cfg.clientId,
      redirect_uri: redirect,
      post_logout_redirect_uri: redirect,
      response_type: 'code',
      scope: cfg.scope,
      extraQueryParams: cfg.audience ? { audience: cfg.audience } : {},
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      automaticSilentRenew: true,
      // Refresh tokens renew without an iframe; nothing else needs a silent-redirect page.
      silent_redirect_uri: redirect,
    });
  }

  /** Completes a redirect back from the issuer (code in the query) or starts the login. */
  async ensureLoggedIn(): Promise<User> {
    const url = new URL(window.location.href);
    if (url.searchParams.has('code') && url.searchParams.has('state')) {
      const user = await this.mgr.signinRedirectCallback();
      const target = (user.state as string | undefined) ?? '#/';
      window.history.replaceState(null, '', window.location.pathname + target);
      return user;
    }
    if (url.searchParams.has('error')) {
      throw new Error(`Login failed: ${url.searchParams.get('error_description') ?? url.searchParams.get('error')}`);
    }
    const user = await this.mgr.getUser();
    if (user && !user.expired) return user;
    if (user?.refresh_token) {
      try {
        const renewed = await this.mgr.signinSilent();
        if (renewed) return renewed;
      } catch {
        /* fall through to a full login */
      }
    }
    await this.mgr.signinRedirect({ state: window.location.hash || '#/' });
    return new Promise<User>(() => {}); // the page navigates away
  }

  async token(): Promise<string> {
    const user = await this.mgr.getUser();
    if (user && !user.expired && (user.expires_in ?? 0) > 30) return user.access_token;
    return this.renew();
  }

  async renew(): Promise<string> {
    try {
      const user = await this.mgr.signinSilent();
      if (user) return user.access_token;
    } catch {
      /* re-login below */
    }
    await this.mgr.signinRedirect({ state: window.location.hash || '#/' });
    return new Promise<string>(() => {});
  }

  async logout(): Promise<void> {
    await this.mgr.signoutRedirect();
  }
}
